import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import SmoothScroll from "./components/layout/SmoothScroll";
import { SiteSettingsProvider } from "./context/SiteSettingsContext";
import { ServicesProvider } from "./context/ServicesContext";
import ScrollToTop from "./components/layout/ScrollToTop";
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import FloatingWhatsApp from "./components/layout/FloatingWhatsApp";
import Home from "./pages/Home";
import About from "./pages/About";
import Services from "./pages/Services";
import ServiceDetail from "./pages/ServiceDetail";
import OurWorks from "./pages/OurWorks";
import Promotions from "./pages/Promotions";
import Blog from "./pages/Blog";
import BlogArticle from "./pages/BlogArticle";
import Faqs from "./pages/Faqs";
import Contact from "./pages/Contact";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import NotFound from "./pages/NotFound";
import "./App.css";

/**
 * The admin panel is code-split.
 *
 * It pulls in the Firebase SDK and the whole management UI — roughly the size
 * of the marketing site again. Loading it lazily means a customer looking for
 * a cleaning quote never downloads any of it.
 */
const AdminApp = lazy(() => import("./admin/AdminApp"));

/**
 * The marketing chrome (header, footer, floating WhatsApp button, smooth
 * scrolling) is wrong for the admin panel, so /admin renders outside it.
 */
/**
 * Sends the old nested service URL to its new root-level home.
 *
 * /services/deep-floor-cleaning also has to become /floor-machine-scrub, so
 * renamed slugs are mapped explicitly rather than just being un-nested.
 */
const RENAMED_SERVICE_SLUGS = {
  "deep-floor-cleaning": "floor-machine-scrub",
};

function LegacyServiceRedirect() {
  const { slug } = useParams();
  const target = RENAMED_SERVICE_SLUGS[slug] ?? slug;
  return <Navigate to={`/${target}`} replace />;
}

function SiteRoutes() {
  return (
    <SmoothScroll>
      <ScrollToTop />
      <Header />
      <main id="main" className="bg-ink text-white">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />

          {/* Projects & Reviews — the brief merges Our Works with reviews. */}
          <Route path="/projects" element={<OurWorks />} />

          <Route path="/promotions" element={<Promotions />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogArticle />} />
          <Route path="/faqs" element={<Faqs />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy-policy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />

          {/*
            Retired routes.
            Redirects rather than deletions: these URLs have been shared and
            may be linked from elsewhere, and a redirect passes on whatever
            search equity they carry instead of dropping it at a 404.
          */}
          <Route path="/why-us" element={<Navigate to="/about" replace />} />
          <Route path="/our-works" element={<Navigate to="/projects" replace />} />
          <Route path="/services/:slug" element={<LegacyServiceRedirect />} />

          {/*
            Service pages sit at the root, per the brief — each is a Google Ads
            landing page. Placed last so it cannot shadow a real page: any
            unmatched single segment is tested against the published services,
            and falls through to the 404 if it is not one.
          */}
          <Route path="/:slug" element={<ServiceDetail />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
      <FloatingWhatsApp />
    </SmoothScroll>
  );
}

function AppRoutes() {
  const { pathname } = useLocation();

  if (pathname === "/admin" || pathname.startsWith("/admin/")) {
    return (
      <Suspense
        fallback={
          <div className="min-h-screen bg-ink flex items-center justify-center text-mutedtext text-sm">
            Loading admin…
          </div>
        }
      >
        <Routes>
          <Route path="/admin/*" element={<AdminApp />} />
        </Routes>
      </Suspense>
    );
  }

  return <SiteRoutes />;
}

export default function App() {
  return (
    <BrowserRouter>
      {/*
        Settings wrap the router so the header, footer and every WhatsApp link
        share one fetch. Inside the router rather than outside it only because
        nothing here needs route context — either position works, but this
        keeps the provider list flat.
      */}
      <SiteSettingsProvider>
        <ServicesProvider>
          <AppRoutes />
        </ServicesProvider>
      </SiteSettingsProvider>
    </BrowserRouter>
  );
}
